import React, { FC, useContext, useEffect } from 'react';
import { zip, intersection, difference, startCase } from 'lodash-es';

import { FluidObject } from 'gatsby-image';

import { ProductInfoContext } from '../../../templates/pdp';
import { ContentSection } from '../../../utils/types';

import ewAnalytics from '../../../utils/analytics';
import { ANALYTICS } from '../../../utils/constants/analytics';
import { addToCart, addToCartDataLayer } from '../../../utils/cartHelpers';

import MarkerSection from './MarkerSection';
import ProductDetails from './ProductDetails';
import AddToCart from './AddToCart';
import ComparisonSubSection from './ComparisonSubSection';

import * as S from './styles/comparisonContentStyles';

interface ComparisonContent {
  title: string;
  listOfContentSections: ContentSection[];
  desktopImages: { fluid: FluidObject }[];
  leftSlug: string;
  rightSlug: string;
  setOpen: (e: boolean) => void;
}

type Product = {
  title: string;
  price: string;
  variantId: number;
  slug: string;
  image: FluidObject;
};

const ComparisonContent: FC<ComparisonContent> = ({
  title,
  listOfContentSections,
  desktopImages,
  leftSlug,
  rightSlug,
  setOpen,
}) => {
  const productInfo = useContext(ProductInfoContext);

  // It is expected that the measured items in pdpLeft are a strict
  // subset of those measured by pdpRight
  const [pdpLeft, pdpRight] = listOfContentSections.map(section => {
    return section.listOfContentSections.map(s => formatItems(s));
  });

  const compare = zip(pdpLeft, pdpRight).map(([left, right]) =>
    joinMarkerData(left, right),
  );

  const productInfoLeft = productInfo.productsBySlug[leftSlug];
  const productInfoRight = productInfo.productsBySlug[rightSlug];
  const PICombined = [productInfoLeft, productInfoRight].map((product, idx) => {
    return {
      title: product.name,
      price: product.price,
      image: desktopImages[idx],
      slug: product.slug,
      variantId: product.variantId,
    };
  });

  useEffect(() => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.VIEWED_PAGE,
      data: {
        label: ANALYTICS.LABELS.PDP_UPGRADE_MODAL,
      },
    });
  }, []);

  const handleAddToCart = (
    { title, price, variantId }: Omit<Product, 'image' | 'slug'>,
    isUpgrade: boolean,
  ) => () => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.ADD_TO_CART,
      data: {
        label: isUpgrade
          ? ANALYTICS.LABELS.ADD_TO_CART_UPGRADE_MODAL_WITH_UPGRADE
          : ANALYTICS.LABELS.ADD_TO_CART_UPGRADE_MODAL_NO_UPGRADE,
      },
    });
    addToCart(variantId, undefined, undefined, 1);
    addToCartDataLayer(variantId, title, price);
    setOpen(false);
  };

  return (
    <>
      <S.Title>{title}</S.Title>
      <S.CompContainer>
        {/* Product Details Sections */}
        <ProductDetails
          image={PICombined[0].image}
          title={PICombined[0].title}
          price={PICombined[0].price}
          slug={PICombined[0].slug}
        />
        <ProductDetails
          image={PICombined[1].image}
          title={PICombined[1].title}
          price={PICombined[1].price}
          slug={PICombined[1].slug}
        />
        {/* Main Comparison Sections */}
        {compare.map(({ title, image, comp }: ComparisonSubSection, idx) => (
          <ComparisonSubSection
            title={title}
            image={image}
            comp={comp}
            key={`comp-${idx}`}
          />
        ))}
      </S.CompContainer>

      {/* Bottom AddToCart Section */}
      <S.AddToCartWrapper>
        <AddToCart
          data-testid={`upgrade-add-to-cart-left`}
          title={PICombined[0].title}
          price={PICombined[0].price}
          slug={PICombined[0].slug}
          isUpgrade={false}
          onClick={handleAddToCart(
            {
              title: PICombined[0].title,
              price: PICombined[0].price,
              variantId: PICombined[0].variantId,
            },
            false,
          )}
        />
        <AddToCart
          data-testid={`upgrade-add-to-cart-right`}
          title={PICombined[1].title}
          price={PICombined[1].price}
          slug={PICombined[1].slug}
          isUpgrade={true}
          onClick={handleAddToCart(
            {
              title: PICombined[1].title,
              price: PICombined[1].price,
              variantId: PICombined[1].variantId,
            },
            true,
          )}
        />
      </S.AddToCartWrapper>
    </>
  );
};

export type Comparison = {
  intersect: string[];
  left: string[];
  right: string[];
};

export default ComparisonContent;

function joinMarkerData(left?: MarkerSection, right?: MarkerSection) {
  return {
    title: left?.title || right?.title || '',
    image: left?.image || right?.image || '',
    comp: compareMarkers(left?.markers || [], right?.markers || []),
  };
}

function compareMarkers(a: string[] = [], b: string[] = []): Comparison {
  const intersect = intersection(a, b);
  const left = difference(a, intersect);
  const right = difference(b, intersect);

  return { intersect, left, right };
}

function formatItems({
  title,
  image,
  description,
}: Omit<ContentSection, 'listOfContentSections'>) {
  const formattedItemNames = markersFromString(description.description);
  return {
    image: image.file.url || '',
    title: title,
    markers: formattedItemNames,
  };
}

function markersFromString(markersString: string): string[] {
  return markersString.split('\n').map(normalize);
}

function normalize(word: string): string {
  return startCase(word.trim());
}
